/**
 * Role-specific onboarding copy — the ONE source of truth (#431).
 *
 * These sentences are the promise we make a new staffer in the invite
 * EMAIL (`apps/server/src/services/invite-email.ts` →
 * `packages/email-templates/src/owner-invite.tsx`). The first-login
 * welcome card in the admin-app (`components/WelcomeCard.tsx`) echoes the
 * SAME sentence back on the home screen they land on, so the orientation
 * the email promised is the first thing they actually see.
 *
 * WHY THIS FILE EXISTS (and why the copy is not defined in either place):
 * the email lives on the server, the card lives in the browser bundle. A
 * hand-copied map in the admin-app would drift the moment anyone edits one
 * side — and the whole point of the card is that it matches the email
 * verbatim. So the strings live HERE, in a browser-safe leaf module with
 * no Node/Express/Prisma dependencies, imported by:
 *
 *   - the server:    `@ferro/core` (via rbac/index.js) — re-exported from
 *                    `services/invite-email.ts` for its existing callers.
 *   - the admin-app: `@ferro/core/src/rbac/role-invite-copy.js` — the same
 *                    leaf-import convention `lib/rbac-matrix.ts` already
 *                    uses for `@ferro/core/src/rbac/matrix.js`.
 *
 * PURITY CONTRACT: keep this module dependency-free apart from the
 * `RoleSlug` type import. It is bundled into the browser.
 */

import type { RoleSlug } from './matrix.js';

/**
 * One-sentence "here's what you'll do in the app" blurb, per role (#431 —
 * the invite email otherwise gives a new staffer zero orientation beyond
 * their role name).
 *
 * Deliberately covers only the six non-owner roles: the Owner welcome
 * sentence already says "invited to manage {tenant}", which is orientation
 * enough on its own. Unknown/legacy role slugs get no blurb — the email
 * paragraph and the welcome card are both omitted rather than guessing at
 * unfamiliar duties.
 */
export const ROLE_INVITE_BLURBS: Partial<Record<RoleSlug, string>> = {
  manager:
    "This is where you'll see the full day at a glance — dashboard, schedule, staff, and reports.",
  receptionist:
    "This is where you'll check dogs in and out, manage bookings, and take payments at the front desk.",
  groomer:
    "This is where you'll see your grooming queue each morning, check dogs in and out of appointments, and log grooming notes.",
  walker:
    "This is where you'll see your walk schedule for the day and log each walk as you go.",
  kennel_tech:
    "This is where you'll see the kennel board — who's checked in, their runs, and daily care tasks.",
  accountant:
    "This is where you'll find reports, revenue, and the numbers side of the business.",
};

/**
 * Map a role slug to its onboarding blurb. Returns `undefined` for `owner`
 * and for unrecognized slugs so every caller (invite email, welcome card)
 * omits the copy entirely rather than rendering something generic or
 * wrong. Both surfaces treat `undefined` the same way: no blurb, and — for
 * the welcome card — no card at all.
 */
export function roleBlurbForSlug(slug: string): string | undefined {
  return slug in ROLE_INVITE_BLURBS
    ? ROLE_INVITE_BLURBS[slug as RoleSlug]
    : undefined;
}
